import { Box, Chip, colors, Paper, Typography } from '@material-ui/core';
import React from 'react';
import useStyles from './styles';
import FaceIcon from '@material-ui/icons/Face';
import AppButton from 'components/atoms/Button';
import AddCircleOutlineOutlinedIcon from '@material-ui/icons/AddCircleOutlineOutlined';
import SelectionsModal from './SelectionsListModal';
import { useDispatch } from 'react-redux';
import ModalActions from 'redux/reducers/gl_modal_reducer/actions';
import { ModalIDs } from 'redux/reducers/gl_modal_reducer/types';
import { IModalParams as SelectionListModalParams } from './SelectionsListModal';
import { arrayToObject } from 'utils/arrays';
import useDeepCompareEffect from 'hooks/useDeepCompareEffect';
import _ from 'lodash';

export interface IMultipleSelectProps {
  title: React.ReactNode;
  buttonName: React.ReactNode;
  options: Array<any>;
  keyName: string;
  idKey: string;
  defaultSelectedOptions?: Array<string | number>;
  onSelectionsChange?: (data: Array<string | number>) => void;
  error?: boolean;
}

const MultipleSelect = ({
  title,
  buttonName,
  defaultSelectedOptions,
  keyName,
  options,
  onSelectionsChange,
  idKey,
  error,
}: IMultipleSelectProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const optionsMap = React.useMemo(() => {
    return arrayToObject(options, idKey);
  }, [options]);
  const [defaultOptions, setDefaultOptions] = React.useState<
    typeof defaultSelectedOptions
  >(defaultSelectedOptions);
  const defaultOptionsMap = React.useMemo(() => {
    return arrayToObject(defaultOptions ?? []);
  }, [defaultOptions]);

  useDeepCompareEffect(() => {
    setDefaultOptions(defaultSelectedOptions);
  }, [defaultSelectedOptions]);

  React.useEffect(() => {
    onSelectionsChange && onSelectionsChange(defaultOptions ?? []);
  }, [defaultOptions]);

  return (
    <Box style={{ width: '100%' }}>
      <SelectionsModal />
      <Paper
        style={{
          padding: '0.6rem',
          borderRadius: 12,
          marginBottom: '0.4rem',
          width: '100%',
          borderColor: error ? colors['red'][600] : undefined,
        }}
        variant="outlined"
      >
        <Typography
          style={{ color: error ? colors['red'][600] : undefined }}
          className={classes.chipsTitle}
        >
          {title}
        </Typography>

        <Box className={classes.chipContainer}>
          {defaultOptions?.map((el, index) => (
            <Chip
              color="secondary"
              icon={<FaceIcon />}
              label={_.get(optionsMap, `${el}.${keyName}`)} // optionsMap[el as any][keyName]
              clickable
              key={index}
              onDelete={() => {
                setDefaultOptions(
                  defaultOptions.filter((_, _index) => _index !== index)
                );
              }}
            />
          ))}
        </Box>
      </Paper>
      <AppButton
        variant="text"
        startIcon={<AddCircleOutlineOutlinedIcon />}
        text={buttonName}
        style={{ color: error ? 'red' : undefined }}
        onClick={() => {
          dispatch(
            ModalActions.openModalAction({
              modalID: ModalIDs.MODAL_OPTIONS,
              params: {
                options: options ?? [],
                idName: idKey,
                keyName,
                defaultOptionsMap,
                onSave: (elements) => {
                  setDefaultOptions(elements);
                },
              } as SelectionListModalParams,
            })
          );
        }}
      />
    </Box>
  );
};

export default React.memo(MultipleSelect);
